import axios from 'axios';
import { User, UserCreate, Product, ProductCreate, ScanResult } from '../types';

// Dynamically determine API URL based on current hostname
const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // If accessing from localhost, use localhost
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return 'http://localhost:8001';
  }
  
  // For production domain analizez.eu, use the same domain with /api path
  if (window.location.hostname === 'analizez.eu') {
    return 'https://analizez.eu/api';
  }
  
  // Otherwise, use the same hostname as the frontend but with port 8001
  return `http://${window.location.hostname}:8001`;
};

const API_BASE_URL = getApiBaseUrl();

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: () => {
    // Use direct auth URL (not through API_BASE_URL)
    window.location.href = '/auth/google';
  },

  handleCallback: (): { access_token: string; user: any } | null => {
    const urlParams = new URLSearchParams(window.location.search);
    const accessToken = urlParams.get('access_token');
    const userData = urlParams.get('user');

    if (accessToken && userData) {
      return {
        access_token: accessToken,
        user: JSON.parse(userData)
      };
    }
    return null;
  },
  
  logout: async () => {
    try {
      await api.post('/auth/logout');
    } finally {
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
  },

  getCurrentUser: async (): Promise<User> => {
    const response = await api.get('/users/me');
    return response.data;
  },
};

export const usersAPI = {
  getAll: async (): Promise<User[]> => {
    const response = await api.get('/users/');
    return response.data;
  },

  create: async (userData: UserCreate): Promise<User> => {
    const response = await api.post('/users/', userData);
    return response.data;
  },

  updateRole: async (userId: string, role: 'admin' | 'regular'): Promise<void> => {
    await api.put(`/users/${userId}/role`, { role });
  },

  updateStatus: async (userId: string, isActive: boolean): Promise<void> => {
    await api.put(`/users/${userId}/status`, { is_active: isActive });
  },

  delete: async (userId: string): Promise<void> => {
    await api.delete(`/users/${userId}`);
  },

  getRequestLimits: async (): Promise<any[]> => {
    const response = await api.get('/users/request-limits');
    return response.data;
  },

  updateRequestLimit: async (userId: string, limitData: { monthly_request_limit?: number }): Promise<void> => {
    await api.put(`/users/${userId}/request-limit`, limitData);
  },

  resetMonthlyRequests: async (): Promise<void> => {
    await api.post('/users/reset-monthly-requests');
  },
};

export const productsAPI = {
  getAll: async (search?: string): Promise<Product[]> => {
    const params = search ? { search } : {};
    const response = await api.get('/products/', { params });
    return response.data;
  },

  getById: async (productId: string): Promise<Product> => {
    const response = await api.get(`/products/${productId}`);
    return response.data;
  },

  getByBarcode: async (barcode: string): Promise<Product> => {
    const response = await api.get(`/products/barcode/${barcode}`);
    return response.data;
  },

  create: async (productData: ProductCreate): Promise<Product> => {
    const response = await api.post('/products/', productData);
    return response.data;
  },

  update: async (productId: string, productData: Partial<ProductCreate>): Promise<Product> => {
    const response = await api.put(`/products/${productId}`, productData);
    return response.data;
  },

  delete: async (productId: string): Promise<void> => {
    await api.delete(`/products/${productId}`);
  },

  scanProduct: async (barcode: string): Promise<ScanResult> => {
    const response = await api.post(`/products/scan/${barcode}`);
    return response.data;
  },

  getMyScanHistory: async (): Promise<any[]> => {
    const response = await api.get('/products/history/my');
    return response.data;
  },

  analyzePhotos: async (barcode: string, ingredientsPhoto: string, nutritionalPhoto: string): Promise<any> => {
    const response = await api.post('/products/analyze-photos', {
      barcode,
      ingredients_photo: ingredientsPhoto,
      nutritional_photo: nutritionalPhoto
    });
    return response.data;
  },
};

export default api;
